import React, { Fragment, useEffect, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';

import styles from './index.module.scss';
import WarningMessages from './warning-messages';
import MfaStepUpBanner from './mfa-step-up-banner';

import { openSidebarMenuSelector } from 'selectors/sidebar-menu';
import { rootSiteSelector, siteFromRootSiteSelector } from 'selectors/root-site';
import { useGetContents } from 'hooks/contents';
import { HEADER_AND_FOOTER_PREFIX_KEY } from 'constants/content-keys';
import userActions from 'actions/user-actions';
import HeadTags from 'components/head-tags';
import { useDetectLanguage } from 'hooks/location';
import { usePrefetchCustomPage } from 'hooks/custom-page';
import { useColorPalette } from 'hooks/color-palette';
import StoreRedirect from 'components/store-redirect';
import { useImageCornerRoundness } from 'hooks/site-configurations';
import SkipLinks from 'components/skip-links';
import SkeletonLayout from 'components/skeleton-layout';
import { useLazyComponent } from 'hooks/use-lazy-component';
import AnalyticsProvider from 'components/analytics-provider';

const SidebarMenu = React.lazy(() => import('components/sidebar-menu'));
const DesignsOverlay = React.lazy(() => import('components/designs-overlay'));

const ContainerLayout = ({ container: Container, title, customPageId, staticJsonUrl }) => {
  const { siteSlug } = useParams();
  const dispatch = useDispatch();

  const { siteId, colorPalette, homePage, headerAndFooterStaticJsonUrl } = useSelector(
    siteSlug ? siteFromRootSiteSelector(siteSlug) : rootSiteSelector,
  );
  const openSidebarMenu = useSelector(openSidebarMenuSelector);

  useEffect(() => {
    if (!siteSlug) {
      dispatch(userActions.clearMembership());
    }
  }, [dispatch, siteSlug]);

  useDetectLanguage();
  usePrefetchCustomPage(customPageId, siteId, staticJsonUrl);
  usePrefetchCustomPage(homePage?.id, siteId, homePage?.staticJsonUrl);
  useGetContents(siteId, HEADER_AND_FOOTER_PREFIX_KEY, { staticJsonUrl: headerAndFooterStaticJsonUrl });
  useColorPalette(colorPalette);
  useImageCornerRoundness();

  const { Component: Header, isLoading: headerLoading } = useLazyComponent('header', () => import('components/header'));
  const { Component: Footer, isLoading: footerLoading } = useLazyComponent('footer', () => import('components/footer'));

  const layoutReady = !headerLoading && !footerLoading;

  return (
    <Fragment>
      <SkipLinks />
      <StoreRedirect />
      <div className={classNames(styles.layout, openSidebarMenu && styles.container_with_menu_sidebar)}>
        {layoutReady ? (
          <Fragment>
            <Suspense fallback={null}>
              <SidebarMenu />
            </Suspense>
            <WarningMessages />
            <MfaStepUpBanner />
            <HeadTags title={title} />
            <Header />
            <main id='main' role='main'>
              <Suspense fallback={<SkeletonLayout />}>
                <Container />
              </Suspense>
            </main>
            <Suspense fallback={null}>
              <DesignsOverlay />
            </Suspense>
            <Footer />
          </Fragment>
        ) : (
          <SkeletonLayout />
        )}
      </div>
      <AnalyticsProvider />
    </Fragment>
  );
};

ContainerLayout.propTypes = {
  container: PropTypes.elementType,
  title: PropTypes.string,
  customPageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  staticJsonUrl: PropTypes.string,
};

export default ContainerLayout;
